import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { ShippingRate, ShippingZone } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyRateForm, fromRate, toRateBody } from '../api/mappers';
import { useCreateRate, useUpdateRate } from '../hooks/use-shipping-mutations';
import {
  rateFormSchema,
  type RateFormInput,
  type RateFormOutput,
} from '../schemas/rate-form-schema';
import { RATE_KINDS, rateFieldsFor } from '../schemas/rate-rules';
import { rateCurrencyError } from '../schemas/shipping-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'kind',
  'price',
  'minWeightGrams',
  'maxWeightGrams',
  'minSubtotal',
  'maxSubtotal',
  'deliveryDaysMin',
  'deliveryDaysMax',
  'requiresSignature',
  'isActive',
  'position',
]);

/** `body.price.currency` belongs to the price field. */
const mapField = (field: string) => field.replace(/\.currency$|\.amount$/, '');

type MoneyField = 'price' | 'minSubtotal' | 'maxSubtotal';
type WholeField = 'minWeightGrams' | 'maxWeightGrams' | 'deliveryDaysMin' | 'deliveryDaysMax';

export interface RateDialogProps {
  storeId: string;
  currency: string;
  zone: ShippingZone;
  /** null creates in the zone; a rate edits it. */
  rate: ShippingRate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The inputs follow the kind: flat has a price, weight and price add a band, free_over a threshold. */
export function RateDialog({ storeId, currency, zone, rate, open, onOpenChange }: RateDialogProps) {
  const { t } = useTranslation(['shipping', 'common']);
  const create = useCreateRate(storeId);
  const update = useUpdateRate(storeId);
  const message = useErrorMessage();
  const nextPosition = zone.rates.reduce((max, r) => Math.max(max, r.position + 1), 0);
  const form = useForm<RateFormInput, unknown, RateFormOutput>({
    resolver: useI18nResolver(rateFormSchema),
    defaultValues: rate ? fromRate(rate) : emptyRateForm(currency, nextPosition),
  });
  const kind = form.watch('kind');
  const fields = rateFieldsFor(kind);
  const isPending = create.isPending || update.isPending;

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const body = toRateBody(values, currency);
      if (rate) await update.mutateAsync({ id: rate.id, body });
      else await create.mutateAsync({ zoneId: zone.id, body });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      const currencyError = rateCurrencyError(error);
      form.setError('root.server', {
        type: 'server',
        message: currencyError
          ? t(currencyError.key, { currency: currencyError.currency })
          : message(error),
      });
    }
  });

  const moneyField = (name: MoneyField, label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <MoneyInput
              currency={currency}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              aria-invalid={fieldState.invalid}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const wholeField = (name: WholeField, label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input inputMode="numeric" autoComplete="off" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {rate
              ? t('shipping:rate.editDialog.title')
              : t('shipping:rate.create.title', { zone: zone.name })}
          </DialogTitle>
          <DialogDescription>{t('shipping:rate.create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping:rate.fields.name')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping:rate.fields.kind')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.clearErrors(['price', 'minWeightGrams', 'minSubtotal']);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RATE_KINDS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {t(`shipping:rate.kind.${k}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`shipping:rate.kindHint.${kind}`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {fields.price ? moneyField('price', t('shipping:rate.fields.price')) : null}
            {fields.weightBand ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {wholeField('minWeightGrams', t('shipping:rate.fields.minWeightGrams'))}
                {wholeField('maxWeightGrams', t('shipping:rate.fields.maxWeightGrams'))}
              </div>
            ) : null}
            {fields.subtotalBand ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {moneyField('minSubtotal', t('shipping:rate.fields.minSubtotal'))}
                {moneyField('maxSubtotal', t('shipping:rate.fields.maxSubtotal'))}
              </div>
            ) : null}
            {fields.threshold
              ? moneyField('minSubtotal', t('shipping:rate.fields.freeOverThreshold'))
              : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {wholeField('deliveryDaysMin', t('shipping:rate.fields.deliveryDaysMin'))}
              {wholeField('deliveryDaysMax', t('shipping:rate.fields.deliveryDaysMax'))}
            </div>
            <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping:rate.fields.position')}</FormLabel>
                    <FormControl>
                      <Input type="number" inputMode="numeric" min={0} step={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col gap-2">
                <FormField
                  control={form.control}
                  name="requiresSignature"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border px-3 py-2">
                      <FormLabel className="font-normal">
                        {t('shipping:rate.fields.requiresSignature')}
                      </FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border px-3 py-2">
                      <FormLabel className="font-normal">
                        {t('shipping:rate.fields.isActive')}
                      </FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : rate
                    ? t('common:actions.save')
                    : t('shipping:rate.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
